import React from 'react';
import { Edit2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import toast from 'react-hot-toast';
import loadingAnimation from '../assets/Loading.json';
import { ShineBorder } from './ui/shine-border';
import { PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';

const CampaignCard = ({
  campaign,
  onEdit,
  canEdit = true,
  useOverwriteDates = false, // Overwrite tarihleri kullanılsın mı
  showHideEndDate = false, // hideShowEndDate kontrolü yapılsın mı
  sortingMode = false
}) => {
  const navigate = useNavigate();

  // Tarih gösterimini belirle
  const renderStartDate = () => {
    if (useOverwriteDates && campaign.overwriteStartDate && campaign.overwriteStartDate !== '-') {
      return (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {campaign.overwriteStartDate}
          </div>
          {campaign.startDate && campaign.startDate !== '-' && (
            <div className="text-xs text-gray-400 mt-0.5">{campaign.startDate}</div>
          )}
        </div>
      );
    }
    return <span className="font-medium text-gray-900">{campaign.startDate || '-'}</span>;
  };

  const renderEndDate = () => {
    if (showHideEndDate && campaign.hideShowEndDate) {
      return <span className="font-medium text-gray-900">Gizli</span>;
    }
    
    if (useOverwriteDates && campaign.overwriteEndDate && campaign.overwriteEndDate !== '-') {
      return (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {campaign.overwriteEndDate}
          </div>
          {campaign.endDate && campaign.endDate !== '-' && (
            <div className="text-xs text-gray-400 mt-0.5">{campaign.endDate}</div>
          )}
        </div>
      );
    }
    return <span className="font-medium text-gray-900">{campaign.endDate || '-'}</span>;
  };

  // Status badge'ini belirle
  const renderStatusBadge = () => {
    const isActive = campaign.isActiveApp === 1 || campaign.status === 'active';
    
    if (!isActive) return null;

    return (
      <div className="absolute top-4 right-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          campaign.isActiveApp === 1
            ? 'bg-white border-2 border-green-500 text-green-600'
            : (campaign.status === 'active'
              ? 'bg-green-500 text-white'
              : 'bg-gray-500 text-white')
        }`}>
          {campaign.isActiveApp === 1
            ? 'Yayında'
            : (campaign.status === 'active' ? 'Aktif' : 'Pasif')
          }
        </span>
      </div>
    );
  };

  // Eski isim/description gösterimi
  const renderDescription = () => {
    const description = campaign.discountOfferDescription || campaign.description;
    if (!description) return null;

    return (
      <p className="text-sm text-gray-500 mb-6 bg-gray-50 rounded-lg p-2 w-fit">
        {description}
      </p>
    );
  };


  return (
    <ShineBorder
      borderWidth={2}
      duration={4}
      gradient="from-blue-500 via-red-500 to-teal-400"
      className="w-full h-full"
    >
    <div key={campaign.id} className="h-full flex flex-col bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      {/* Görsel */}
      {(campaign.campaignImage || campaign.image) ? (
        <div className="relative h-48">
          <img
            src={campaign.campaignImage || campaign.image}
            alt=""
            className="w-full h-full object-cover"
          />
          {renderStatusBadge()}
        </div>
      ) : (
        <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 h-48 flex items-center justify-center">
          <div className="text-center">
            <Lottie
              animationData={loadingAnimation}
              loop={true}
              style={{ 
                width: '160px', 
                height: '160px'
              }}
            />
            <p className="text-gray-500 font-medium -mt-5">Buraya görsel gelecek</p>
          </div>
          {renderStatusBadge()}
        </div>
      )}

      {/* İçerik */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{campaign.title}</h3>

          {renderDescription()}
        </div>

        <div className="space-y-2 mb-4">
          {/* Başlangıç Tarihi */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Başlangıç:</span>
            {useOverwriteDates ? renderStartDate() : (
              <span className="font-medium text-gray-900">{campaign.startDate || '-'}</span>
            )}
          </div>

          {/* Bitiş Tarihi */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Bitiş:</span>
            {useOverwriteDates ? renderEndDate() : (
              <span className="font-medium text-gray-900">{campaign.endDate || '-'}</span>
            )}
          </div>

          {/* Mağaza Kodu */}
          {campaign.offices && campaign.offices.trim() && (
            <div className="flex items-start justify-between text-sm pt-4">
              <span className="text-gray-500">Mağaza Kodu:</span>
              <span className="font-medium text-gray-900 text-right max-w-[60%] break-words">
                {campaign.offices}
              </span>
            </div>
          )}
        </div>

        {sortingMode ? (
          <div className="pt-4 border-t border-gray-200 text-xs text-gray-500 font-medium">
            Sıralamak icin kartı sürükleyin
          </div>
        ) : (
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => navigate(`/kampanyalar/detay/${campaign.id}`)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-medium"
            >
              <Eye /> Detay
            </button>
            <button
              onClick={() => {
                if (!canEdit) {
                  toast.error(PERMISSION_DENIED_MESSAGE);
                  return;
                }
                onEdit(campaign);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-xl transition-colors font-medium ${permissionGuardClass(canEdit)} ${canEdit ? 'hover:bg-green-100' : ''}`}
              aria-disabled={!canEdit}
            >
              <Edit2 /> Düzenle
            </button>
          </div>
        )}
      </div>
    </div>
    </ShineBorder>
  );
};

export default CampaignCard;

